import React, { useState } from 'react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, Row, Col, Statistic, Select, DatePicker } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ShoppingCartOutlined, DollarOutlined, InboxOutlined, TeamOutlined } from '@ant-design/icons';

const { RangePicker } = DatePicker;

// Sample data
const salesData = [
  { month: 'Jan', sales: 45000, purchase: 32000, profit: 13000 },
  { month: 'Feb', sales: 52000, purchase: 35000, profit: 17000 },
  { month: 'Mar', sales: 48000, purchase: 33000, profit: 15000 },
  { month: 'Apr', sales: 61000, purchase: 41000, profit: 20000 },
  { month: 'May', sales: 55000, purchase: 38000, profit: 17000 },
  { month: 'Jun', sales: 67000, purchase: 44000, profit: 23000 },
];

const stockData = [
  { category: 'Electronics', stock: 450, reorderLevel: 200 },
  { category: 'Clothing', stock: 680, reorderLevel: 300 },
  { category: 'Food', stock: 320, reorderLevel: 400 },
  { category: 'Furniture', stock: 180, reorderLevel: 150 },
  { category: 'Books', stock: 520, reorderLevel: 250 },
];

const distributorData = [
  { name: 'Distributor A', value: 35, orders: 145 },
  { name: 'Distributor B', value: 25, orders: 98 },
  { name: 'Distributor C', value: 20, orders: 76 },
  { name: 'Distributor D', value: 12, orders: 45 },
  { name: 'Distributor E', value: 8, orders: 32 },
];

const weeklyData = [
  { day: 'Mon', sales: 8500, purchase: 6200 },
  { day: 'Tue', sales: 9200, purchase: 6800 },
  { day: 'Wed', sales: 7800, purchase: 5900 },
  { day: 'Thu', sales: 10500, purchase: 7400 },
  { day: 'Fri', sales: 12000, purchase: 8200 },
  { day: 'Sat', sales: 11500, purchase: 7800 },
  { day: 'Sun', sales: 8200, purchase: 6100 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const AllDashboardChart = () => {
  const [timeRange, setTimeRange] = useState('month');

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>Sales Analytics Dashboard</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Select defaultValue="month" style={{ width: 120 }} onChange={setTimeRange}>
            <Select.Option value="week">Week</Select.Option>
            <Select.Option value="month">Month</Select.Option>
            <Select.Option value="year">Year</Select.Option>
          </Select>
          <RangePicker />
        </div>
      </div>

      {/* KPI Cards */}
      {/* <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Sales"
              value={328000}
              precision={0}
              prefix={<DollarOutlined />}
              suffix="USD"
              valueStyle={{ color: '#3f8600' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#52c41a' }}>
              <ArrowUpOutlined /> 12.5% from last month
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total Purchases"
              value={223000}
              precision={0}
              prefix={<ShoppingCartOutlined />}
              suffix="USD"
              valueStyle={{ color: '#cf1322' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#cf1322' }}>
              <ArrowUpOutlined /> 8.2% from last month
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Stock Items"
              value={2150}
              prefix={<InboxOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#faad14' }}>
              <ArrowDownOutlined /> 3 items below reorder level
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Distributors"
              value={5}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#52c41a' }}>
              <ArrowUpOutlined /> 396 total orders
            </div>
          </Card>
        </Col>
      </Row> */}

      {/* Sales vs Purchase Line Chart */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={16}>
          <Card title="Sales & Purchase Trends" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="sales" stroke="#52c41a" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="purchase" stroke="#1890ff" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="profit" stroke="#722ed1" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Distributor Share" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={distributorData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {distributorData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Stock Levels Bar Chart */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title="Stock Levels by Category" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stockData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="stock" fill="#1890ff" />
                <Bar dataKey="reorderLevel" fill="#ff7875" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Weekly Performance" bordered={false}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="sales" stackId="1" stroke="#52c41a" fill="#52c41a" fillOpacity={0.6} />
                <Area type="monotone" dataKey="purchase" stackId="2" stroke="#ff7875" fill="#ff7875" fillOpacity={0.6} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Distributor Performance */}
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card title="Distributor Performance" bordered={false}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={distributorData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="orders" fill="#722ed1" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AllDashboardChart;